import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FormError } from '@/components/ui/form-error';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from '@/lib/utils';
import { nameRule, optionalText, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';

const schema = z.object({
  name: nameRule('Project Name', 200, 2),
  clientName: optionalText(200),
  siteLocation: optionalText(500),
  projectStartDate: z.string().min(1, 'Project Start Date is required'),
  projectCloseDate: z.string().min(1, 'Project Close Date is required'),
  description: optionalText(2000),
}).superRefine((data, ctx) => {
  if (data.projectStartDate && data.projectCloseDate && data.projectStartDate > data.projectCloseDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['projectCloseDate'], message: 'Close Date cannot be earlier than Start Date' });
  }
});
type FormData = z.infer<typeof schema>;

export interface CreatedProject {
  id: string;
  name: string;
  clientName?: string;
}

interface AddProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: CreatedProject) => void;
}

/**
 * "Add Project" quick-create modal opened from the Project Access workflow. It
 * reuses the existing project creation API (POST /projects) and the same master
 * fields as the Projects form, so no duplicate schema/table is introduced. On
 * success the parent refreshes its project list and can preselect the new
 * project for the current user. On failure the modal stays open and shows the
 * standard safe error (inline field errors + toast).
 */
export function AddProjectModal({ open, onOpenChange, onCreated }: AddProjectModalProps) {
  const { addToast } = useToast();
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', clientName: '', siteLocation: '', projectStartDate: '', projectCloseDate: '', description: '' },
  });

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const createProject = useMutation({
    mutationFn: (d: FormData) => api.post('/projects', d),
    onSuccess: (_res, d) => {
      addToast('Project created', 'success');
      const data = (_res as { data?: { id?: string } })?.data;
      onCreated({ id: data?.id || '', name: d.name, clientName: d.clientName });
      onOpenChange(false);
    },
    onError: (err: any) => handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Project</DialogTitle>
          <DialogDescription>
            Create a new project and assign it to this user immediately. The project will also appear in the project list.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => createProject.mutate(d), onInvalid)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="min-w-0">
              <Label htmlFor="add-project-name">Project Name *</Label>
              <Input
                id="add-project-name"
                placeholder="e.g. ZYDUS PTC"
                {...register('name')}
                aria-invalid={errors.name ? true : undefined}
                aria-describedby={errors.name ? 'add-project-name-error' : undefined}
                className={cn(errors.name && 'border-error')}
              />
              <FormError id="add-project-name-error" message={errors.name?.message} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="add-project-client">Client</Label>
              <Input id="add-project-client" {...register('clientName')} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="add-project-site-location">Site Location</Label>
              <Input id="add-project-site-location" {...register('siteLocation')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <Label htmlFor="add-project-start-date">Project Start Date *</Label>
                <DatePicker
                  id="add-project-start-date"
                  {...register('projectStartDate')}
                  aria-invalid={errors.projectStartDate ? true : undefined}
                  aria-describedby={errors.projectStartDate ? 'add-project-start-date-error' : undefined}
                  className={cn(errors.projectStartDate && 'border-error')}
                />
                <FormError id="add-project-start-date-error" message={errors.projectStartDate?.message} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="add-project-close-date">Project Close Date *</Label>
                <DatePicker
                  id="add-project-close-date"
                  {...register('projectCloseDate')}
                  aria-invalid={errors.projectCloseDate ? true : undefined}
                  aria-describedby={errors.projectCloseDate ? 'add-project-close-date-error' : undefined}
                  className={cn(errors.projectCloseDate && 'border-error')}
                />
                <FormError id="add-project-close-date-error" message={errors.projectCloseDate?.message} />
              </div>
            </div>
            <div className="min-w-0">
              <Label htmlFor="add-project-description">Description</Label>
              <Textarea id="add-project-description" {...register('description')} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={createProject.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={createProject.isPending}>
              Create Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}